import {
  Box,
  Button,
  FormControl,
  Input,
  Text,
  useColorModeValue,
  Badge,
  useToast,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  VStack,
  HStack,
  Switch,
  FormLabel,
  Spinner,
  Select,
  Wrap,
  WrapItem,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import { getCustomer, getProducts, createOrderCuota, createBatchOrders, createManualOrder, getCredentials, getConfig } from "../../../../services/woo";
import MultiMonthSelector from "./MultiMonthSelector";
import _ from 'lodash';

function BatchUsersOrderCreate({ title, customers, onOrderCreated }) {
  const toast = useToast();
  const titleColor = useColorModeValue("teal.300", "teal.200");
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("gray.200", "gray.800");
  const [selectedMonths, setSelectedMonths] = useState(() => {
    try {
      const saved = localStorage.getItem('batch-order-settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Migrar de month simple a array de meses
        if (parsed.selectedMonths && Array.isArray(parsed.selectedMonths)) {
          return parsed.selectedMonths;
        } else if (parsed.month) {
          return [parsed.month];
        }
      }
    } catch (error) {
      console.error('Error loading selectedMonths from localStorage:', error);
    }
    return [new Date().toISOString().slice(0, 7)];
  });
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failedOrders, setFailedOrders] = useState([]);
  const [ordersCreated, setOrdersCreated] = useState([]);
  const [silentMode, setSilentMode] = useState(() => {
    try {
      const saved = localStorage.getItem('batch-order-settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.silentMode || false;
      }
    } catch (error) {
      console.error('Error loading silentMode from localStorage:', error);
    }
    return false;
  });
  const [isAsyncProcessing, setIsAsyncProcessing] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [asyncProgress, setAsyncProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState('');
  const [monthResults, setMonthResults] = useState([]);
  const [processingMonthIndex, setProcessingMonthIndex] = useState(-1);
  // Detectar si hay meses de diciembre seleccionados para la opción especial
  const hasDecemberMonths = selectedMonths.some(month => month.endsWith('-12'));
  const [diciembreCuota1, setDiciembreCuota1] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [quantity, setQuantity] = useState(1);

  // Cobro manual
  const [chargeType, setChargeType] = useState('mensualidad'); // 'mensualidad' | 'manual'
  const [manualItemName, setManualItemName] = useState('');
  const [manualAmount, setManualAmount] = useState('');

  // Calcular fecha de vencimiento basada en el primer mes seleccionado
  const firstMonth = selectedMonths.length > 0 ? selectedMonths[0] : new Date().toISOString().slice(0, 7);
  const fechaVencimiento = new Date(`${firstMonth}-01`);
  fechaVencimiento.setDate(fechaVencimiento.getDate() + 14); // ✅ CORREGIDO: +14 días = día 15
  const fechaVencimientoString = fechaVencimiento.toISOString().split('T')[0];

  // Helper para determinar si es mensualidad regular (no multa/interés)
  const isRegularMensualidad = () => {
    if (!selectedConcept) return true; // Por defecto, tratar como mensualidad
    
    // Detectar productos que NO son mensualidades regulares
    const conceptoLower = selectedConcept.toLowerCase();
    return !conceptoLower.includes('interés') && 
           !conceptoLower.includes('interes') && 
           !conceptoLower.includes('mora') &&
           !conceptoLower.includes('multa') &&
           !conceptoLower.includes('recargo');
  };

  // Helper para calcular monto total
  const calculateTotalAmount = () => {
    if (!selectedConcept) return 0;
    const selectedProduct = products.find(p => p.name === selectedConcept);
    return selectedProduct ? selectedProduct.price * quantity : 0;
  };

  // Función para guardar configuraciones en localStorage
  const saveSettings = (newSelectedMonths, newSilentMode) => {
    try {
      const settings = {
        selectedMonths: newSelectedMonths,
        silentMode: newSilentMode,
        // Mantener compatibilidad con month individual por si acaso
        month: newSelectedMonths.length > 0 ? newSelectedMonths[0] : new Date().toISOString().slice(0, 7),
        timestamp: Date.now()
      };
      localStorage.setItem('batch-order-settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings to localStorage:', error);
    }
  };

  // Efecto para guardar configuraciones cuando cambien
  useEffect(() => {
    saveSettings(selectedMonths, silentMode);
  }, [selectedMonths, silentMode]);

  // Efecto para resetear cantidad cuando se cambia a mensualidad regular
  useEffect(() => {
    if (selectedConcept && isRegularMensualidad()) {
      setQuantity(1); // Resetear a 1 para mensualidades regulares
    }
  }, [selectedConcept]);

  // Efecto para cargar productos y establecer concepto inicial
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const productsData = await getProducts();
        setProducts(productsData || []);
        
        // Para un solo customer, usar su concepto actual por defecto
        // Para múltiples customers, dejar vacío para forzar selección
        if (customers.length === 1) {
          const currentConcept = _.find(customers[0].meta_data, { key: 'suscription_name' })?.value;
          setSelectedConcept(currentConcept || '');
        } else {
          setSelectedConcept(''); // Forzar selección para múltiples usuarios
        }
      } catch (error) {
        console.error('Error loading products:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar las mensualidades disponibles",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoadingProducts(false);
      }
    };
    
    loadProducts();
  }, [customers, toast]);

  // Función para verificar el estado de un trabajo asíncrono
  const checkJobStatus = async (jobId) => {
    const credentials = getCredentials();
    const apiUrl = getConfig(credentials, "WP_API_URL") || "https://pagos.blaspascal.cl/wp-json";
    
    try {
      const response = await fetch(`${apiUrl}/datofy/v1/job-status/${jobId}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking job status:', error);
      return null;
    }
  };

  // Función para monitorear el progreso de un trabajo asíncrono — retorna Promise
  const monitorAsyncJob = (jobId) => {
    return new Promise((resolve, reject) => {
      let lastUpdatedAt = null;
      let unchangedPolls = 0;
      const STUCK_THRESHOLD = 15; // 15 polls × 2s = 30s sin actualizar → colgado

      const interval = setInterval(async () => {
        const status = await checkJobStatus(jobId);

        if (status) {
          setAsyncProgress(status.progress || 0);

          if (status.status === 'processing') {
            if (status.updated_at === lastUpdatedAt) {
              unchangedPolls++;
              if (unchangedPolls >= STUCK_THRESHOLD) {
                setProcessingStatus('processing_stuck');
              } else {
                setProcessingStatus('processing');
              }
            } else {
              lastUpdatedAt = status.updated_at;
              unchangedPolls = 0;
              setProcessingStatus('processing');
            }
          } else {
            setProcessingStatus(status.status || 'processing');
            unchangedPolls = 0;
          }

          if (status.status === 'completed') {
            clearInterval(interval);
            resolve(status);
          }

          if (status.status === 'pending') {
            const timeDiff = Date.now() - new Date(status.created_at).getTime();
            if (timeDiff > 30000) {
              setProcessingStatus('pending_stuck');
            }
          }

          if (status.status === 'failed') {
            clearInterval(interval);
            reject(new Error(status.error || 'Error al procesar las órdenes'));
          }
        }
      }, 2000);
    });
  };

  // Función para forzar ejecución de trabajo pendiente/colgado
  const forceProcessJob = async (jobId) => {
    const credentials = getCredentials();
    const apiUrl = getConfig(credentials, "WP_API_URL") || "https://pagos.blaspascal.cl/wp-json";

    try {
      const response = await fetch(`${apiUrl}/datofy/v1/force-process/${jobId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      // 202 = programado vía cron, ok = ejecutado (modo anterior)
      if (response.ok || response.status === 202) {
        toast({
          title: "Ejecución programada",
          description: "El trabajo fue programado para ejecutarse ahora. Monitorando progreso...",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
        setProcessingStatus('processing');
      } else {
        const body = await response.json().catch(() => ({}));
        toast({
          title: "No se pudo forzar",
          description: body.error || `Error del servidor (${response.status})`,
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      // 504 / ERR_FAILED = el servidor tardó demasiado respondiendo, pero pudo haber
      // aceptado la solicitud. No mostrar error — el polling detectará si arrancó.
      console.warn('Force job request ended without response (posible 504):', error.message);
      toast({
        title: "Solicitud enviada",
        description: "El servidor no confirmó a tiempo, pero el trabajo podría estar en proceso. Verificando progreso...",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      // Mantener polling activo — si el job arrancó, se detectará en el siguiente ciclo
      setProcessingStatus('processing');
    }
  };

  // Crea cobros manuales para cada customer × cada mes seleccionado
  const createManualOrderBatch = async () => {
    if (!manualItemName.trim()) {
      toast({ title: "Campo requerido", description: "Escribe el nombre del ítem", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    const parsedAmount = parseFloat(manualAmount);
    if (!manualAmount || isNaN(parsedAmount) || parsedAmount <= 0) {
      toast({ title: "Monto inválido", description: "Ingresa un monto mayor a 0", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (selectedMonths.length === 0) {
      toast({ title: "Mes requerido", description: "Selecciona al menos un mes", status: "warning", duration: 3000, isClosable: true });
      return;
    }

    setLoading(true);
    setProgress(0);
    setFailedOrders([]);
    setOrdersCreated([]);

    const total = customers.length * selectedMonths.length;
    let done = 0;
    let allCreated = [];
    let allFailed = [];

    for (const month of selectedMonths) {
      for (const customer of customers) {
        try {
          const groupName = _.find(customer.meta_data, { key: 'group_name' })?.value || '';
          const result = await createManualOrder({
            customerId: customer.id,
            itemName:   manualItemName.trim(),
            amount:     parsedAmount,
            mes:        month,
            username:   customer.username   || '',
            email:      customer.email      || '',
            firstName:  customer.first_name || '',
            lastName:   customer.last_name  || '',
            groupName,
          });
          if (result.success) {
            allCreated.push(result.data);
          } else {
            allFailed.push({ customer, error: result.message || 'Error desconocido' });
          }
        } catch (err) {
          allFailed.push({ customer, error: err.message || 'Error al crear' });
        }
        done++;
        setProgress((done / total) * 100);
      }
    }

    setOrdersCreated(allCreated);
    setFailedOrders(allFailed);
    setLoading(false);

    toast({
      title: "Proceso completado",
      description: `${allCreated.length} cobro${allCreated.length !== 1 ? 's' : ''} manual${allCreated.length !== 1 ? 'es' : ''} creado${allCreated.length !== 1 ? 's' : ''}${allFailed.length > 0 ? `, ${allFailed.length} con errores` : ''}`,
      status: allCreated.length > 0 ? "success" : "warning",
      duration: 8000,
      isClosable: true,
    });

    if (onOrderCreated) onOrderCreated();
  };

  // Procesa meses uno por uno en orden secuencial
  const createOrderSuscriptionBatch = async () => {
    if (selectedMonths.length === 0) {
      toast({ title: "Meses requeridos", description: "Debes seleccionar al menos un mes para generar cobros", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    if (customers.length > 1 && !selectedConcept) {
      toast({ title: "Concepto requerido", description: "Debes seleccionar un concepto de mensualidad para aplicar a todos los usuarios", status: "warning", duration: 4000, isClosable: true });
      return;
    }

    setLoading(true);
    setProgress(0);
    setFailedOrders([]);
    setOrdersCreated([]);

    const monthsToProcess = selectedMonths.map(month =>
      (month.endsWith('-12') && diciembreCuota1) ? month + '-31' : month
    );

    setMonthResults(monthsToProcess.map(m => ({ month: m, status: 'pending', success: 0, failed: 0 })));

    let conceptToUse = null;
    if (customers.length === 1) {
      const currentConcept = _.find(customers[0].meta_data, { key: 'suscription_name' })?.value;
      if (selectedConcept && selectedConcept !== currentConcept) conceptToUse = selectedConcept;
    } else if (selectedConcept) {
      conceptToUse = selectedConcept;
    }

    const shouldUseAsync = customers.length > 50;
    let allOrdersCreated = [];
    let allFailed = [];

    for (let i = 0; i < monthsToProcess.length; i++) {
      const month = monthsToProcess[i];
      setProcessingMonthIndex(i);
      setMonthResults(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'processing' } : r));

      try {
        const result = await createBatchOrders(customers, [month], silentMode, shouldUseAsync, conceptToUse, quantity);

        if (shouldUseAsync && result.job_id) {
          setJobId(result.job_id);
          setIsAsyncProcessing(true);
          setAsyncProgress(0);
          setProcessingStatus('starting');

          const jobResult = await monitorAsyncJob(result.job_id);
          const success = jobResult.results?.success_count || 0;
          const failed = jobResult.results?.failed_count || 0;
          allOrdersCreated = [...allOrdersCreated, ...(jobResult.results?.orders?.filter(Boolean) || [])];
          if (failed > 0) allFailed.push({ customer: { first_name: month, last_name: '' }, error: `${failed} no se pudieron crear` });
          setMonthResults(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'done', success, failed } : r));
          setIsAsyncProcessing(false);
          setJobId(null);
        } else {
          const orders = result.orders || result;
          const success = result.success_count || orders.filter(Boolean).length;
          const failed = orders.filter(o => o === null).length;
          allOrdersCreated = [...allOrdersCreated, ...orders.filter(Boolean)];
          if (failed > 0) allFailed.push({ customer: { first_name: month, last_name: '' }, error: `${failed} no se pudieron crear` });
          setMonthResults(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'done', success, failed } : r));
        }
      } catch (error) {
        console.error(`Error procesando mes ${month}:`, error);
        setMonthResults(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'error', error: error.message } : r));
        allFailed.push({ customer: { first_name: month, last_name: '' }, error: error.message || 'Error al procesar' });
        setIsAsyncProcessing(false);
      }

      setProgress(((i + 1) / monthsToProcess.length) * 100);
    }

    setOrdersCreated(allOrdersCreated);
    setFailedOrders(allFailed);
    setProcessingMonthIndex(-1);
    setLoading(false);

    toast({
      title: "Proceso completado",
      description: `${monthsToProcess.length} mes(es) procesados. ${allOrdersCreated.length} cobros creados${allFailed.length > 0 ? `, ${allFailed.length} con errores` : ''}`,
      status: allOrdersCreated.length > 0 ? "success" : "warning",
      duration: 10000,
      isClosable: true,
    });

    if (onOrderCreated) onOrderCreated();
  };

  const createOrderSuscription = async () => {
    setLoading(true);
    setProgress(0);
    setFailedOrders([]);
    const products = await getProducts();
    const totalOperations = customers.length * selectedMonths.length;
    let completedOperations = 0;

    for (let i = 0; i < customers.length; i++) {
      const customer = customers[i];
      try {
        const getCustomerInfo = await getCustomer(customer.id);
        const { meta_data } = getCustomerInfo.data;
        // Usar el concepto seleccionado o el concepto actual del customer
        const customer_suscription = selectedConcept || _.find(meta_data, { key: 'suscription_name' })?.value;
        const product_found = _.find(products, { name: customer_suscription });

        if (!customer_suscription || !product_found) {
          setFailedOrders(prev => [...prev, {
            customer: customer,
            error: !customer_suscription ? "No tiene suscripción" : "Plan no existe"
          }]);
          continue;
        }

        // Crear una orden por cada mes seleccionado
        for (const monthValue of selectedMonths) {
          const meta_data_order = [
            { key: "mes", value: monthValue },
            { key: 'concepto', value: customer_suscription },
            { key: 'monto', value: product_found?.price * quantity },
            { key: "cantidad", value: quantity },
            { key: "fecha_vencimiento", value: fechaVencimientoString },
            { key: 'usuario', value: customer.username},
            { key: 'email', value: customer.email},
            { key: 'order_type', value: 'subscription_payment' },
            { key: 'order_for', value: 'single'},
            { key: 'order_from', value: 'pagoexpress_client'},
          ];

          const orderCreated = await createOrderCuota(customer, product_found, meta_data_order, quantity);

          if (orderCreated) {
            setOrdersCreated(prev => [...prev, orderCreated.create]);
          }
          
          completedOperations++;
          setProgress((completedOperations / totalOperations) * 100);
        }

      } catch (error) {
        setFailedOrders(prev => [...prev, {
          customer: customer,
          error: "Error al crear orden"
        }]);
      }
      
      // El progreso se actualiza dentro del bucle de meses
    }
    
    setLoading(false);
    if (onOrderCreated) {
      onOrderCreated();
    }
  }

  const summaryBg = useColorModeValue("blue.50", "blue.900");
  const summaryBorder = useColorModeValue("blue.200", "blue.600");
  const summaryTextColor = useColorModeValue("blue.800", "blue.100");

  return (
    <VStack spacing={4} align="stretch" pb={2}>
      {/* Header */}
      {customers.length === 1 ? (
        <Box>
          <Text fontSize="lg" fontWeight="semibold">{customers[0]?.first_name} {customers[0]?.last_name}</Text>
          <Text fontSize="sm" color="gray.500">{customers[0]?.email}</Text>
          <Text fontSize="sm"><strong>Concepto actual:</strong> {_.find(customers[0].meta_data, { key: 'suscription_name' })?.value}</Text>
        </Box>
      ) : (
        <HStack>
          <Badge fontSize="0.9em" colorScheme="teal" px={2} py={1}>{customers.length} usuarios</Badge>
          <Text fontSize="lg" fontWeight="semibold">Generar cobro mensual</Text>
        </HStack>
      )}

      <Divider />

      {/* Layout principal de 2 columnas en desktop */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="start">

        {/* COLUMNA IZQUIERDA: Selector de meses */}
        <Box>
          <Text fontWeight="semibold" color={textColor} mb={3}>
            Seleccionar meses
          </Text>
          <MultiMonthSelector
            selectedMonths={selectedMonths}
            onChange={setSelectedMonths}
            disabled={loading || isAsyncProcessing || loadingProducts}
            allowQuickSelect={true}
          />
        </Box>

        {/* COLUMNA DERECHA: Configuración + botón */}
        <VStack spacing={3} align="stretch">

          {/* Resumen compacto de operación */}
          {selectedMonths.length > 0 && (
            <Box
              p={3}
              bg={summaryBg}
              borderRadius="md"
              border="1px"
              borderColor={summaryBorder}
            >
              <Text fontSize="sm" fontWeight="bold" color={summaryTextColor} mb={2}>
                Resumen de operación
              </Text>
              <HStack justify="space-between" wrap="wrap" spacing={3}>
                <Box textAlign="center">
                  <Text fontSize="xl" fontWeight="bold" color={summaryTextColor}>{customers.length}</Text>
                  <Text fontSize="xs" color="gray.500">usuarios</Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="xl" fontWeight="bold" color={summaryTextColor}>{selectedMonths.length}</Text>
                  <Text fontSize="xs" color="gray.500">meses</Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="xl" fontWeight="bold" color={summaryTextColor}>{customers.length * selectedMonths.length}</Text>
                  <Text fontSize="xs" color="gray.500">cobros</Text>
                </Box>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={2}>
                <strong>Vence:</strong> {fechaVencimientoString}
              </Text>
              {selectedMonths.length > 1 && (
                <Wrap spacing={1} mt={2}>
                  {selectedMonths.map((month) => {
                    const [year, monthNum] = month.split('-');
                    const monthNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                    return (
                      <WrapItem key={month}>
                        <Badge size="sm" colorScheme="blue">
                          {monthNames[parseInt(monthNum) - 1]} {year}
                        </Badge>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              )}
              {customers.length > 50 && (
                <Alert status="warning" size="sm" borderRadius="md" mt={2} py={1} px={2}>
                  <AlertIcon boxSize="14px" />
                  <Text fontSize="xs">Cada mes se procesará en segundo plano (asíncrono).</Text>
                </Alert>
              )}
            </Box>
          )}

          {/* Toggle tipo de cobro */}
          <HStack spacing={0} borderRadius="md" overflow="hidden" border="1px" borderColor="gray.200">
            <Button
              flex={1}
              size="sm"
              borderRadius={0}
              colorScheme={chargeType === 'mensualidad' ? 'teal' : 'gray'}
              variant={chargeType === 'mensualidad' ? 'solid' : 'ghost'}
              onClick={() => setChargeType('mensualidad')}
              isDisabled={loading || isAsyncProcessing}
            >
              Mensualidad
            </Button>
            <Button
              flex={1}
              size="sm"
              borderRadius={0}
              colorScheme={chargeType === 'manual' ? 'teal' : 'gray'}
              variant={chargeType === 'manual' ? 'solid' : 'ghost'}
              onClick={() => setChargeType('manual')}
              isDisabled={loading || isAsyncProcessing}
            >
              Manual
            </Button>
          </HStack>

          {/* Selector de concepto/mensualidad */}
          {chargeType === 'mensualidad' && (
            <>
              <FormControl>
                <FormLabel fontWeight="semibold" color={textColor} mb={1}>
                  Concepto de mensualidad
                </FormLabel>
                <Select
                  value={selectedConcept}
                  onChange={(e) => setSelectedConcept(e.target.value)}
                  size="md"
                  placeholder={loadingProducts ? "Cargando conceptos..." : "Selecciona un concepto"}
                  isDisabled={loadingProducts || loading || isAsyncProcessing}
                >
                  {products.map((product) => (
                    <option key={product.id} value={product.name}>
                      {product.name} - ${product.price}
                    </option>
                  ))}
                </Select>
                {customers.length === 1 && selectedConcept && selectedConcept !== _.find(customers[0].meta_data, { key: 'suscription_name' })?.value && (
                  <Text fontSize="xs" color="orange.500" mt={1}>
                    Se cambiará el concepto a "{selectedConcept}"
                  </Text>
                )}
                {customers.length > 1 && selectedConcept && (
                  <Text fontSize="xs" color="blue.500" mt={1}>
                    Se aplicará "{selectedConcept}" a todos los usuarios
                  </Text>
                )}
              </FormControl>

              {/* Campo de cantidad - Solo para intereses/multas/recargos */}
              {selectedConcept && !isRegularMensualidad() && (
                <FormControl>
                  <FormLabel fontWeight="semibold" color={textColor} mb={1}>
                    Cantidad
                  </FormLabel>
                  <HStack spacing={3} align="center">
                    <Input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      max="99"
                      size="md"
                      placeholder="1"
                      isDisabled={loading || isAsyncProcessing}
                      maxW="100px"
                    />
                    <Box>
                      <Text fontSize="xs" color="gray.500">Total por usuario</Text>
                      <Text fontSize="lg" fontWeight="bold" color={titleColor}>
                        ${new Intl.NumberFormat('es-CL').format(calculateTotalAmount())}
                      </Text>
                    </Box>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Unidades de {selectedConcept.toLowerCase()} por usuario
                  </Text>
                </FormControl>
              )}
            </>
          )}

          {/* Campos cobro manual */}
          {chargeType === 'manual' && (
            <>
              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color={textColor} mb={1}>
                  Ítem
                </FormLabel>
                <Input
                  placeholder="Ej: Matrícula, Material didáctico..."
                  value={manualItemName}
                  onChange={(e) => setManualItemName(e.target.value)}
                  isDisabled={loading}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color={textColor} mb={1}>
                  Monto
                </FormLabel>
                <HStack spacing={3} align="center">
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="0"
                    value={manualAmount}
                    onChange={(e) => setManualAmount(e.target.value)}
                    isDisabled={loading}
                    maxW="160px"
                  />
                  {manualAmount && parseFloat(manualAmount) > 0 && (
                    <Box>
                      <Text fontSize="xs" color="gray.500">Por usuario × mes</Text>
                      <Text fontSize="lg" fontWeight="bold" color={titleColor}>
                        ${new Intl.NumberFormat('es-CL').format(parseFloat(manualAmount))}
                      </Text>
                    </Box>
                  )}
                </HStack>
              </FormControl>
            </>
          )}

          {/* Opciones: Modo silencioso */}
          <HStack justify="space-between" p={3} bg={bgColor} borderRadius="md">
            <Box>
              <Text fontWeight="semibold" color={textColor} fontSize="sm">
                Modo silencioso
              </Text>
              <Text fontSize="xs" color="gray.500">
                {silentMode ? 'Sin emails de notificación' : 'Con emails de notificación'}
              </Text>
            </Box>
            <Switch
              id="silent-mode"
              isChecked={silentMode}
              onChange={(e) => setSilentMode(e.target.checked)}
              colorScheme="teal"
              size="md"
            />
          </HStack>

          {/* Diciembre Cuota 1 */}
          {hasDecemberMonths && (
            <HStack justify="space-between" p={3} bg={bgColor} borderRadius="md">
              <Box>
                <Text fontWeight="semibold" color={textColor} fontSize="sm">
                  Diciembre Cuota 1
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Matrícula año siguiente (dic. seleccionados)
                </Text>
              </Box>
              <Switch
                id="diciembre-cuota-1"
                isChecked={diciembreCuota1}
                onChange={(e) => setDiciembreCuota1(e.target.checked)}
                colorScheme="orange"
                size="md"
              />
            </HStack>
          )}

          {/* Botón principal */}
          <Button
            colorScheme="teal"
            size="lg"
            onClick={() => chargeType === 'manual' ? createManualOrderBatch() : createOrderSuscriptionBatch()}
            isLoading={loading}
            disabled={
              loading || selectedMonths.length === 0 ||
              (chargeType === 'mensualidad' && (loadingProducts || (customers.length > 1 && !selectedConcept))) ||
              (chargeType === 'manual' && (!manualItemName.trim() || !manualAmount || parseFloat(manualAmount) <= 0))
            }
            mt={1}
          >
            {chargeType === 'manual'
              ? `GENERAR COBRO MANUAL${silentMode ? ' · SILENCIOSO' : ''}`
              : `GENERAR COBRO${diciembreCuota1 ? ' · DIC CUOTA 1' : ''}${silentMode ? ' · SILENCIOSO' : ''}`
            }
          </Button>

        </VStack>
      </SimpleGrid>

      {/* Cola de meses — visible durante y después del procesamiento */}
      {monthResults.length > 0 && (
        <Box mt={2}>
          <Text fontWeight="semibold" fontSize="sm" mb={2} color={textColor}>
            Cola de procesamiento
          </Text>
          <VStack spacing={1} align="stretch">
            {monthResults.map((r, idx) => {
              const baseMonth = r.month.replace('-31', '');
              const [year, monthNum] = baseMonth.split('-');
              const monthNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
              const label = `${monthNames[parseInt(monthNum) - 1]} ${year}${r.month.endsWith('-31') ? ' (Dic C1)' : ''}`;
              return (
                <HStack key={r.month} p={2} bg={bgColor} borderRadius="md" justify="space-between">
                  <HStack spacing={2}>
                    {r.status === 'pending' && <Badge colorScheme="gray" fontSize="xs">En cola</Badge>}
                    {r.status === 'processing' && (
                      <>
                        <Spinner size="xs" color={processingStatus === 'processing_stuck' ? 'red.400' : 'blue.400'} />
                        <Badge colorScheme={processingStatus === 'processing_stuck' ? 'red' : 'blue'} fontSize="xs">
                          {processingStatus === 'processing_stuck' ? 'Colgado' :
                           isAsyncProcessing ? `${Math.round(asyncProgress)}%` : 'Procesando'}
                        </Badge>
                      </>
                    )}
                    {r.status === 'done' && <Badge colorScheme="green" fontSize="xs">Listo</Badge>}
                    {r.status === 'error' && <Badge colorScheme="red" fontSize="xs">Error</Badge>}
                    <Text fontSize="sm">{label}</Text>
                  </HStack>
                  <HStack spacing={2}>
                    {r.status === 'done' && (
                      <Text fontSize="xs" color="gray.500">{r.success} cobros{r.failed > 0 ? `, ${r.failed} fallaron` : ''}</Text>
                    )}
                    {r.status === 'error' && (
                      <Text fontSize="xs" color="red.400" maxW="180px" isTruncated>{r.error}</Text>
                    )}
                    {r.status === 'processing' && isAsyncProcessing &&
                      ['pending', 'pending_stuck', 'processing_stuck'].includes(processingStatus) && (
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => forceProcessJob(jobId)}>
                        Forzar
                      </Button>
                    )}
                  </HStack>
                </HStack>
              );
            })}
          </VStack>
          {loading && (
            <Box mt={3}>
              <Progress
                value={progress}
                colorScheme={processingStatus === 'pending_stuck' ? 'red' : 'teal'}
                borderRadius="md"
                hasStripe={loading}
                isIndeterminate={processingStatus === 'starting' || processingStatus === 'pending'}
              />
              {isAsyncProcessing && jobId && (
                <HStack mt={1} spacing={2}>
                  <Spinner size="xs" />
                  <Text fontSize="xs" color="gray.500">Job: {jobId}</Text>
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}

      {failedOrders.length > 0 && !loading && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Errores en la generación</AlertTitle>
            <AlertDescription display="block">
              {failedOrders.map((fail, index) => (
                <Text key={index} fontSize="sm">
                  {fail.customer.first_name} {fail.customer.last_name}: {fail.error}
                </Text>
              ))}
            </AlertDescription>
          </Box>
          <CloseButton
            position="absolute"
            right="8px"
            top="8px"
            onClick={() => setFailedOrders([])}
          />
        </Alert>
      )}

      {customers.length > 50 && !loading && monthResults.length === 0 && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Procesamiento asíncrono</AlertTitle>
            <AlertDescription>
              {customers.length} usuarios — cada mes se procesará en segundo plano para evitar timeouts.
            </AlertDescription>
          </Box>
        </Alert>
      )}
    </VStack>
  );
}

export default BatchUsersOrderCreate;